import { isHidden, sendEvent } from "../helpers/util";
import { BRANDS, determineBrandFromURL } from "../helpers/brand";

/* Header hides on scroll on. Shows on scroll up. 
If at top of page, header is transparent. Otherwise background theme is off-white */

export const HEADER_EVENT_NAME = "js-header";

export const header = () => {
  /* Get all block grids and get theme used in first one (highest on page) */
  const brand = determineBrandFromURL();
  const main = document.querySelector("main")!;
  const blockGridElements = main.querySelectorAll("[data-theme]");
  const blockGridTheme =
    blockGridElements[0]?.getAttribute("data-theme") || "bg--default";

  /* Get header and add background theme to match highest block grid element on page. 
  Set active theme as the same - this makes it easier to target later */
  const header = document.querySelector("header");

  if (!header) {
    return;
  }

  header.classList.add(blockGridTheme);
  header.setAttribute("js-active-theme", blockGridTheme);

  /* Get default themes for scrolling and if in front of an image */
  const headerScrollTheme =
    header.getAttribute("js-scroll-theme") || blockGridTheme;
  let headerImgTheme = header.getAttribute("js-img-theme") || blockGridTheme;

  /* Set other consts */
  let headerThreshold = 40;
  let lastScrollTop = document.documentElement.scrollTop;
  let tickingResize = false;
  let tickingScroll = false;

  const snackbar = document.querySelector("[js-snackbar]");
  const headerFeatureImg = document.querySelector<HTMLElement>(
    "[js-header-Feature-img]"
  );

  if (headerFeatureImg && brand !== BRANDS.WILDE) {
    /* If header is a feature image, adjust threshold to height of header */
    headerThreshold = headerFeatureImg.offsetHeight;
  } else if (snackbar) {
    /* Has snackbar, set to higher threshold */
    headerThreshold = 100;
  }

  /* 
    ***
    Change theme depending on screen size and if header has an image background
    ***
  */
  const resizeHandler = () => {
    /* Get bounding area of header and header image */
    const headerArea = header.getBoundingClientRect();
    const headerImg = document.querySelector<HTMLElement>("[js-header-img]");
    const headerImgArea = headerImg
      ?.querySelector("img, video")
      ?.getBoundingClientRect();

    /* Check if header image is not visible */
    if (isHidden(headerImg)) {
      /* Reset header theme back to block grid theme */
      header.setAttribute("js-active-theme", blockGridTheme);

      /* If currently scrolled down the page, don't change the theme class */
      if (header.classList.contains("bg--theme-card")) {
        return;
      }

      header.classList.remove(headerImgTheme);
      header.classList.add(blockGridTheme);
    } else {
      /* Check bounding area to see if header is overlapping with header image */
      /* OR if feature image exists. This is sticky to the top and will always overlap the header banner */
      if (
        (headerImgArea &&
          headerImgArea.top + window.scrollY <= headerArea.bottom &&
          headerImgArea.bottom + window.scrollY >= headerArea.bottom) ||
        headerFeatureImg
      ) {
        if (
          headerImgArea!.left <= headerArea.left &&
          headerImgArea!.right < headerArea.right &&
          brand === BRANDS.WILDE
        ) {
          /* img only covers left half */
          /* Replace block grid theme */
          headerImgTheme = "wilde--half-header-left";
        } else if (
          headerImgArea!.left > headerArea.left &&
          headerImgArea!.right >= headerArea.right &&
          brand === BRANDS.WILDE
        ) {
          /* img only covers left half */
          /* Replace block grid theme */
          headerImgTheme = "wilde--half-header-right";
        }

        /* Replace block grid theme */
        header.setAttribute("js-active-theme", headerImgTheme);

        /* If currently scrolled down the page, don't change the theme class */
        if (header.classList.contains("bg--theme-card")) {
          return;
        }

        header.classList.remove(blockGridTheme);
        header.classList.add(headerImgTheme);
      } else {
        /* If currently scrolled down the page, don't change the theme class */
        if (header.classList.contains("bg--theme-card")) {
          return;
        }
        header.classList.add(blockGridTheme);
        header.classList.remove(headerImgTheme);
      }
    }
  };

  /* 
    ***
    Handle scroll hide/show events
    ***
  */
  const scrollHandler = () => {
    const nextScrollTop = document.documentElement.scrollTop;

    /* is the header currently hidden? */
    const isHidden = header.classList.contains("header-hide");

    /* Get active theme. This gets updated on screen resize */
    const headerActiveTheme = header.getAttribute("js-active-theme")!;

    if (nextScrollTop === lastScrollTop && nextScrollTop >= headerThreshold) {
      /* If refreshes page while part way down, show background on header */
      header.classList.remove(headerActiveTheme);
      header.classList.add("bg--theme-card", headerScrollTheme);
      header.setAttribute("aria-hidden", "false");
    } else if (
      nextScrollTop > lastScrollTop &&
      nextScrollTop >= headerThreshold &&
      !isHidden
    ) {
      header.classList.add("header-hide");
      header.setAttribute("aria-hidden", "true");

      sendEvent(HEADER_EVENT_NAME, { status: "hide" });
    } else if (nextScrollTop < lastScrollTop) {
      /* if we are scrolling up... */
      if (isHidden) {
        /* Show header with a filled background */
        header.classList.remove("header-hide");
        header.classList.remove(headerActiveTheme);
        header.classList.add("bg--theme-card", headerScrollTheme);
        header.setAttribute("aria-hidden", "false");

        sendEvent(HEADER_EVENT_NAME, { status: "show" });
      }

      /* does the header have a background fill? */
      const hasBackgroundFill = header.classList.contains("bg--theme-card");

      if (nextScrollTop <= headerThreshold && hasBackgroundFill) {
        // if the header has a solid background, and we are <compactHeaderThreshold>px or less from the top, remove background colour to make transparent
        header.classList.remove("bg--theme-card", headerScrollTheme);
        header.classList.add(headerActiveTheme);
      }
    }

    /* update the 'last' scroll value with the current value */
    lastScrollTop = Math.max(0, document.documentElement.scrollTop);
  };

  /* Run function on scroll */
  /* debounce by only running when ticking is false */
  ["load", "scroll"].forEach((event) =>
    window.addEventListener(event, () => {
      if (!tickingScroll) {
        window.requestAnimationFrame(() => {
          scrollHandler();
          tickingScroll = false;
        });
      }
      tickingScroll = true;
    })
  );

  /* Run function on load and resize */
  /* debounce by only running when ticking is false */
  ["load", "resize"].forEach((event) =>
    window.addEventListener(event, () => {
      if (!tickingResize) {
        window.requestAnimationFrame(() => {
          resizeHandler();
          tickingResize = false;
        });
      }
      tickingResize = true;
    })
  );
};
